/** ยอดนับป้าย Kanban ไม่ตรง — ผู้อนุมัติตัดสินใจรายการที่ Store นับแล้วยอดต่างจากระบบ (`/v1/tag-count-adjustments`) ปริมาณเป็น string ไม่แปลงเป็น Number */

import { useEffect, useState } from 'react';

import { RecordTable } from '../components/RecordTable';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { fetchRecords, postRecord, type RecordRow } from '../../lib/backend/records';
import { stockFailureText, type StockFailure } from '../../lib/inventory/stock';

const MODULE = 'INVENTORY';
const TAG_COUNT_ADJUSTMENTS_PATH = '/v1/tag-count-adjustments';
const PENDING = 'PENDING_APPROVAL';

const STATUS_LABELS: Readonly<Record<string, string>> = {
  PENDING_APPROVAL: 'รออนุมัติ',
  POSTED: 'ปรับสต๊อกแล้ว',
  APPROVED: 'อนุมัติแล้ว',
  REJECTED: 'ปฏิเสธ',
};
const STATUS_FILTERS = ['PENDING_APPROVAL', 'POSTED', 'APPROVED', 'REJECTED', ''] as const;

/** `+5` / `-2` / `0` — ตัดสินจากตัวอักษรของ string เท่านั้น */
function signedDelta(value: unknown): string {
  if (typeof value !== 'string' || value === '') {
    return '—';
  }
  if (value.startsWith('-') || value.startsWith('+')) {
    return value;
  }
  return /^0*(\.0*)?$/.test(value) ? value : `+${value}`;
}

function failureText(failure: StockFailure): string {
  if (failure.status === 'rejected') {
    switch (failure.rule) {
      case 'COUNT_STALE':
        return 'ยอดในระบบเปลี่ยนไปหลังนับ ให้ Store นับใหม่หรือปฏิเสธ';
      case 'TAG_COUNT_STATUS_CHANGED':
        return 'สถานะรายการนี้ถูกเปลี่ยนไปแล้ว โหลดข้อมูลใหม่ให้แล้ว';
      case 'TAG_COUNT_NOT_FOUND':
        return 'ไม่พบรายการนับป้ายนี้';
      case 'NO_MOVEMENT_TYPE_MAPPING':
        return 'ผู้ดูแลยังไม่ได้ตั้ง movement type TAG_COUNT_ADJUST';
    }
  }
  return stockFailureText(failure);
}

function str(row: RecordRow, key: string): string {
  const v = row[key];
  return typeof v === 'string' ? v : '';
}

export interface TagCountAdjustmentScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function TagCountAdjustmentScreen({ permissions, apiBaseUrl, fetchImpl }: TagCountAdjustmentScreenProps) {
  const canApprove = can(permissions, MODULE, 'APPROVE');
  const [status, setStatus] = useState<string>(PENDING);
  const [rows, setRows] = useState<readonly RecordRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<RecordRow | null>(null);
  const [note, setNote] = useState('');
  const [confirmReject, setConfirmReject] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  const buildIo = () => ({
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const result = await fetchRecords(
        TAG_COUNT_ADJUSTMENTS_PATH,
        { status },
        {
          ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
          ...(fetchImpl === undefined ? {} : { fetchImpl }),
        },
      );
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (result.status !== 'ready') {
        setRows([]);
        setError(stockFailureText(result));
        return;
      }
      setRows(result.items);
    })();
    return () => {
      cancelled = true;
    };
  }, [status, reload, apiBaseUrl, fetchImpl]);

  const select = (row: RecordRow) => {
    setSelected(row);
    setNote('');
    setConfirmReject(false);
    setError(null);
  };

  const decide = async (action: 'approve' | 'reject') => {
    if (selected === null) {
      return;
    }
    const trimmed = note.trim();
    if (action === 'reject' && trimmed === '') {
      setError('ต้องระบุหมายเหตุเมื่อปฏิเสธ');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    const result = await postRecord(
      `${TAG_COUNT_ADJUSTMENTS_PATH}/${encodeURIComponent(str(selected, 'adjustment_id'))}/${action}`,
      { expected_status: PENDING, ...(trimmed === '' ? {} : { note: trimmed }) },
      {},
      buildIo(),
    );
    setBusy(false);
    if (result.status !== 'ready') {
      setError(failureText(result));
      if (result.status === 'rejected' && result.rule === 'TAG_COUNT_STATUS_CHANGED') {
        setSelected(null);
        setReload((n) => n + 1);
      }
      return;
    }
    const no = str(selected, 'adjustment_no');
    setNotice(action === 'approve' ? `อนุมัติแล้ว: ${no}` : `ปฏิเสธแล้ว: ${no}`);
    setSelected(null);
    setConfirmReject(false);
    setReload((n) => n + 1);
  };

  const display = rows.map((r) => ({
    ...r,
    status_label: STATUS_LABELS[str(r, 'status')] ?? str(r, 'status'),
    delta_signed: signedDelta(r.delta),
  }));
  const isPending = selected !== null && str(selected, 'status') === PENDING;

  return (
    <div className="cap-page" data-testid="tag-count-adjustments">
      <div className="cap-page__head">
        <h1 className="cap-page__title">ยอดนับป้าย Kanban ไม่ตรง</h1>
      </div>
      <label className="cap-field">
        <span className="cap-field__label">สถานะ</span>
        <select
          className="cap-input"
          aria-label="สถานะ"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setSelected(null);
          }}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === '' ? 'ทั้งหมด' : (STATUS_LABELS[s] ?? s)}
            </option>
          ))}
        </select>
      </label>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="tag-count-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="cap-notice__text" role="status" data-testid="tag-count-notice">
          {notice}
        </p>
      )}
      {loading ? (
        <p role="status">กำลังโหลด…</p>
      ) : (
        <RecordTable
          testId="tag-count-table"
          rows={display}
          columns={[
            { key: 'adjustment_no', label: 'เลขที่' },
            { key: 'tag_code', label: 'ป้าย' },
            { key: 'lot_number', label: 'Lot' },
            { key: 'product_code', label: 'สินค้า' },
            { key: 'location_code', label: 'ที่เก็บ' },
            { key: 'tag_qty', label: 'ยอดในระบบ' },
            { key: 'counted_qty', label: 'นับได้' },
            { key: 'delta_signed', label: 'ต่าง (+/−)' },
            { key: 'reason', label: 'เหตุผล' },
            { key: 'counted_at', label: 'เวลา' },
            { key: 'status_label', label: 'สถานะ' },
          ]}
          rowActions={(row) => (
            <button type="button" className="cap-button cap-button--ghost" onClick={() => select(row)}>
              ดูรายละเอียด {str(row, 'adjustment_no')}
            </button>
          )}
        />
      )}
      {selected !== null && (
        <section className="cap-card" aria-label="รายละเอียดการนับ" data-testid="tag-count-detail">
          <h2 className="cap-notice__title">{str(selected, 'adjustment_no')}</h2>
          <dl>
            <dt>ป้าย / Lot</dt>
            <dd>
              {str(selected, 'tag_code')} / {str(selected, 'lot_number')}
            </dd>
            <dt>สินค้า / ที่เก็บ</dt>
            <dd>
              {str(selected, 'product_code')} / {str(selected, 'location_code')}
            </dd>
            <dt>ยอดในระบบ → นับได้</dt>
            <dd>
              {str(selected, 'tag_qty')} → {str(selected, 'counted_qty')} {str(selected, 'uom_code')} (ต่าง{' '}
              {signedDelta(selected.delta)})
            </dd>
            <dt>ยอดคงเหลือปัจจุบัน</dt>
            <dd>{str(selected, 'balance_qty') || '—'}</dd>
            <dt>เหตุผล</dt>
            <dd>{str(selected, 'reason') || '—'}</dd>
            {str(selected, 'decision_note') !== '' && (
              <>
                <dt>หมายเหตุการตัดสิน</dt>
                <dd>{str(selected, 'decision_note')}</dd>
              </>
            )}
          </dl>
          {canApprove && isPending && (
            <div className="cap-form" data-testid="tag-count-decision">
              <label className="cap-field">
                <span className="cap-field__label">หมายเหตุ (บังคับเมื่อปฏิเสธ)</span>
                <input className="cap-input" aria-label="หมายเหตุ" value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
              <div className="cap-form__actions">
                <button type="button" className="cap-button cap-button--primary" disabled={busy} onClick={() => void decide('approve')}>
                  อนุมัติ
                </button>
                {!confirmReject ? (
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    disabled={busy}
                    onClick={() => {
                      if (note.trim() === '') {
                        setError('ต้องระบุหมายเหตุเมื่อปฏิเสธ');
                        return;
                      }
                      setError(null);
                      setConfirmReject(true);
                    }}
                  >
                    ปฏิเสธ
                  </button>
                ) : (
                  <>
                    <span role="alert">ยืนยันปฏิเสธรายการนี้?</span>
                    <button type="button" className="cap-button cap-button--primary" disabled={busy} onClick={() => void decide('reject')}>
                      ยืนยันปฏิเสธ
                    </button>
                    <button type="button" className="cap-button cap-button--ghost" onClick={() => setConfirmReject(false)}>
                      ยกเลิก
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
